import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { custApi } from "../../lib/customerApi";
import { toast } from "sonner";
import { MePageTitle, MeSection, MeStagger } from "./MeUI";
import {
  BellRinging, ShieldCheck, DeviceMobile, EnvelopeSimple,
  Palette, Globe, CaretRight, SignOut, MapPin,
} from "@phosphor-icons/react";
import { useCustAuth } from "../../lib/customerAuth";
import { enablePush, disablePush, getPushStatus, sendTestPush, isPushSupported } from "../../lib/pushClient";

/**
 * /me/settings — Customer app settings
 * Best-effort — some toggles just persist to /me/preferences if the endpoint
 * exists; otherwise they're kept in local storage.
 */
export default function MeSettings() {
  const { logout } = useCustAuth();
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("me:prefs") || "{}"); }
    catch { return {}; }
  });
  const [push, setPush] = useState({ supported: false, permission: "default", enabled: false, configured: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    getPushStatus().then(setPush).catch(() => {});
  }, []);

  useEffect(() => {
    custApi.get("/me/preferences").then(r => {
      if (r.data) setPrefs(p => ({ ...p, ...r.data }));
    }).catch(() => {});
  }, []);

  const patch = async (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    try { localStorage.setItem("me:prefs", JSON.stringify(next)); } catch {}
    try { await custApi.patch("/me/preferences", { [k]: v }); } catch {}
  };

  const doLogout = () => {
    if (!window.confirm("Sign out of Rescigo Health?")) return;
    logout?.();
    toast.success("Signed out");
    window.location.href = "/me/login";
  };

  return (
    <div className="font-body pb-6" data-testid="me-settings">
      <MePageTitle
        eyebrow="Settings"
        title={<>Your app,<br/><span className="text-[#DC2626]">your way</span></>}
        subtitle="Control notifications, privacy and appearance."
      />

      <MeStagger stagger={40}>
        <MeSection className="mt-2">
          <Group title="Background alarms">
            <div className="px-3 py-3 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                <BellRinging size={18} weight="fill"/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-[#111827] leading-tight">
                  Ring even when the app is closed
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                  {!push.supported
                    ? "Not supported in this browser — install the app for the best experience."
                    : !push.configured
                      ? "Firebase not fully configured yet — ask your admin."
                      : push.enabled
                        ? "You're all set — reminder alarms will ring in the background."
                        : "Turn on to allow Rescigo to wake you up for medicine, even after you close the browser."
                  }
                </div>
                {push.supported && push.configured && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {!push.enabled ? (
                      <button
                        onClick={async () => {
                          setBusy(true);
                          try {
                            await enablePush();
                            toast.success("Background alarms enabled");
                            setPush(await getPushStatus());
                          } catch (e) { toast.error(e.message || "Could not enable"); }
                          finally { setBusy(false); }
                        }}
                        disabled={busy}
                        data-testid="pref-push-enable"
                        className="h-9 px-3 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-xs font-bold">
                        {busy ? "Enabling…" : "Enable background alarms"}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={async () => {
                            setBusy(true);
                            try {
                              const r = await sendTestPush();
                              toast.success(`Sent to ${r.sent} device(s)`);
                            } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
                            finally { setBusy(false); }
                          }}
                          disabled={busy}
                          data-testid="pref-push-test"
                          className="h-9 px-3 border border-[#DC2626] text-[#DC2626] rounded-md text-xs font-bold hover:bg-[#FEE2E2]">
                          Send test alarm
                        </button>
                        <button
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await disablePush();
                              toast.success("Background alarms disabled");
                              setPush(await getPushStatus());
                            } catch (e) { toast.error(e.message || "Could not disable"); }
                            finally { setBusy(false); }
                          }}
                          disabled={busy}
                          data-testid="pref-push-disable"
                          className="h-9 px-3 border border-gray-200 text-[#5C5A55] rounded-md text-xs font-bold">
                          Disable
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Group>

          <Group title="Notifications" className="mt-4">
            <Toggle label="Push notifications" desc="Reminders, orders and health alerts"
                    checked={prefs.push !== false}
                    onChange={(v) => patch("push", v)}
                    icon={BellRinging} testid="pref-push"/>
            <Toggle label="Email digests" desc="Weekly summary in your inbox"
                    checked={prefs.email_digest === true}
                    onChange={(v) => patch("email_digest", v)}
                    icon={EnvelopeSimple} testid="pref-email"/>
            <Toggle label="SMS reminders" desc="Only for critical health/pickup alerts"
                    checked={prefs.sms !== false}
                    onChange={(v) => patch("sms", v)}
                    icon={DeviceMobile} testid="pref-sms"/>
          </Group>

          <Group title="Privacy" className="mt-4">
            <Toggle label="Share location for services" desc="Speeds up pickup & ambulance"
                    checked={prefs.geo !== false}
                    onChange={(v) => patch("geo", v)}
                    icon={MapPin} testid="pref-geo"/>
            <Toggle label="Two-step passcode" desc="Ask a 6-digit code on every login"
                    checked={prefs.two_step === true}
                    onChange={(v) => patch("two_step", v)}
                    icon={ShieldCheck} testid="pref-2fa"/>
          </Group>

          <Group title="Appearance" className="mt-4">
            <Toggle label="Reduced motion" desc="Turn off subtle animations"
                    checked={prefs.reduced_motion === true}
                    onChange={(v) => patch("reduced_motion", v)}
                    icon={Palette} testid="pref-motion"/>
            <NavRow icon={Globe} label="Language" desc="English (India)" to="#" testid="pref-lang"/>
          </Group>

          <div className="mt-6">
            <button
              onClick={doLogout}
              data-testid="me-settings-signout"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-2xl bg-white border border-[#F4C7B9] text-[#DC2626] font-bold text-sm active:scale-[0.99] transition"
            >
              <SignOut size={16} weight="fill"/> Sign out
            </button>
          </div>

          <div className="mt-6 text-center text-[10px] uppercase tracking-[0.2em] text-gray-400">
            Rescigo Health · v2026.02
          </div>
        </MeSection>
      </MeStagger>
    </div>
  );
}

const Group = ({ title, children, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold px-1 mb-2">{title}</div>
    <div className="rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] divide-y divide-gray-50 overflow-hidden">
      {children}
    </div>
  </div>
);

const Toggle = ({ label, desc, checked, onChange, icon: Icon, testid }) => (
  <label className="flex items-center gap-3 px-3 py-3 cursor-pointer" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={18} weight="fill"/>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-bold text-[#111827] leading-tight">{label}</div>
      <div className="text-[11px] text-gray-500 mt-0.5 truncate">{desc}</div>
    </div>
    <div className={`relative w-10 h-6 rounded-full transition-colors ${checked ? "bg-[#DC2626]" : "bg-gray-200"}`}>
      <input type="checkbox" className="sr-only" checked={checked}
             onChange={(e) => onChange(e.target.checked)} />
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}/>
    </div>
  </label>
);

const NavRow = ({ icon: Icon, label, desc, to, testid }) => (
  <Link to={to} data-testid={testid}
        className="flex items-center gap-3 px-3 py-3 hover:bg-[#FEF2F2] transition">
    <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={18} weight="fill"/>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-bold text-[#111827] leading-tight">{label}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{desc}</div>
    </div>
    <CaretRight size={12} className="text-gray-400"/>
  </Link>
);
